'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Hook to calculate scroll margin for virtualized grids.
 *
 * When using multiple virtualized grids or when the grid is not at the top
 * of the scroll container, the scroll margin is necessary to properly
 * calculate the scroll position of the grid.
 *
 * This hook observes the parent node for changes and updates the scroll
 * margin based on the grid's offset from the top.
 */
export const useVirtualizerScrollMargin = () => {
  const scrollMarginRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    if (!scrollMarginRef.current) return;

    const mutationObserver = new MutationObserver(() => {
      setScrollMargin(scrollMarginRef.current?.offsetTop ?? 0);
    });

    const parentNode = scrollMarginRef.current.parentNode;

    if (parentNode) {
      mutationObserver.observe(parentNode, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  return { scrollMargin, scrollMarginRef };
};
